import { useState, useEffect } from 'react'
import { useGet_middleware_auth_dataMutation } from '@store/slices/auth.slice'

import { OMEGATV_MAC_ADDRESS, OMEGATV_DEVICE_ID } from '@utils/vars'

import { useGetItemFromLocalStorage } from '@hooks/app/localStorage/useGetItemFromLocalStorage'
import { useTypedAction } from '@hooks/common/useTypedActions'
import { useTypedSelector } from '@hooks/common/useTypedSelector'
import { useUpdateEffect } from '@hooks/common/useUpdateEffect'
import { TWebosWindow } from '@hooks/auth/webos/useGetWebosModel'
import { IDeviceInfo } from '@hooks/auth/titan/useGetTitanCredentials'

import {
  setMacToLocalStorage,
  setDeviceModelToLocalStorage,
  setDeviceIdToLocalStorage,
} from '@utils/auth/setAuthDataToLocalStorage'

export const useAuth = () => {
  const [isCreated, setIsCreated] = useState<boolean>(false)

  const isAuthenticated = useTypedSelector(
    (state) => state.auth.isAuthenticated
  )

  const { dialogs_error_setErrorCode } = useTypedAction()

  const [getAuthData, { isLoading, data }] =
    useGet_middleware_auth_dataMutation({ fixedCacheKey: 'shared-auth' })

  useEffect(() => {
    switch (__PLATFORM__) {
    case 'webos': {
      import(/* webpackChunkName: 'webos-auth'*/ './webos/devModule').then(
        () =>
          import(
            /* webpackChunkName: 'webos-auth'*/ './webos/useGetWebosModel'
          ).then((module) => {
            const deviceId = useGetItemFromLocalStorage(OMEGATV_MAC_ADDRESS)
            const getWebOSDeviceData = () => {
              const deviceModel = module.useGetWebosModel()
              const webosWindow = window as TWebosWindow
              webosWindow.webOSDev.LGUDID({
                onSuccess: function (res: { id: string }) {
                  // if (window.Sentry && window.Sentry.setTag) {
                  //   window?.Sentry?.setTag('MODEL', deviceModel)
                  //   window?.Sentry?.setTag('SERIAL', res.id)
                  // }
                  if (deviceId) {
                    setIsCreated(true)
                    return
                  }
                  setMacToLocalStorage(res.id)
                  setDeviceModelToLocalStorage(deviceModel)
                  setIsCreated(true)
                },
                onFailure: function () {
                  dialogs_error_setErrorCode('7099')
                },
              })
            }
            if (__MODE__ === 'production') {
              getWebOSDeviceData()
            } else {
              if (deviceId) {
                return setIsCreated(true)
              }
              getWebOSDeviceData()
            }
          })
      )
      break
    }

    case 'tizen': {
      import(
        /* webpackChunkName: "tizen-auth"*/ './tizen/useGetTizenCredentials'
      ).then((module) => {
        const deviceMac = useGetItemFromLocalStorage(OMEGATV_MAC_ADDRESS)
        const data = module.useGetTizenCredentials()
        if (__MODE__ === 'production') {
          if (!data) return dialogs_error_setErrorCode('7099')
          // if (window.Sentry && window.Sentry.setTag) {
          //   window?.Sentry?.setTag('MODEL', data.deviceModel)
          //   window?.Sentry?.setTag('SERIAL', data.mac)
          // }
          if (deviceMac) {
            setIsCreated(true)
            return
          }
          setMacToLocalStorage(data.mac)
          setDeviceModelToLocalStorage(data.deviceModel)
          setIsCreated(true)
        } else {
          if (deviceMac) {
            setIsCreated(true)
            return
          } else {
            if (data) {
              setMacToLocalStorage(data.mac)
              setDeviceModelToLocalStorage(data.deviceModel)
              setIsCreated(true)
            } else {
              dialogs_error_setErrorCode('7099')
            }
          }
        }
      })
      break
    }

    case 'titan': {
      import(
        /* webpackChunkName: "titan-auth"*/ './titan/useGetTitanCredentials'
      ).then((module) => {
        // set sdk script
        const script = document.createElement('script')
        script.src = 'https://partners.titanos.tv/static/device-info-sdk.js'
        script.onload = () => {
          const data = module.useGetTitanCredentials()
          data.onDeviceInfoReady((device: IDeviceInfo) => {
            const { deviceID, mac, platform } = device.Product

            const setDeviceData = () => {
              setDeviceIdToLocalStorage(deviceID)
              setMacToLocalStorage(mac)
              setDeviceModelToLocalStorage(platform)
            }

            const storageDeviceId =
                useGetItemFromLocalStorage(OMEGATV_DEVICE_ID)

            if (__MODE__ === 'production') {
              // can't get device ID from device api
              if (!mac || platform === 'unknown') {
                return dialogs_error_setErrorCode('7099')
              }
              if (storageDeviceId) {
                return setIsCreated(true)
              }
              setDeviceData()
              setIsCreated(true)
            } else {
              if (storageDeviceId) {
                return setIsCreated(true)
              } else {
                if (mac || platform !== 'unknown') {
                  setDeviceData()
                  setIsCreated(true)
                } else {
                  dialogs_error_setErrorCode('7099')
                }
              }
            }
          })
        }
        document.head.appendChild(script)
      })
      break
    }

    case 'desktop': {
      if (__MODE__ === 'production') {
        setIsCreated(true)
      } else {
        const deviceId = useGetItemFromLocalStorage(OMEGATV_MAC_ADDRESS)
        if (deviceId) {
          return setIsCreated(true)
        } else {
          dialogs_error_setErrorCode('7099')
        }
      }
      break
    }

    default:
      break
    }
  }, [])

  useUpdateEffect(() => {
    if (isCreated && !isAuthenticated) getAuthData()
  }, [isCreated, isAuthenticated])

  return { isLoading, data, isAuthenticated }
}
